import React from 'react';
import { AvisoRentaCell } from '../../../components/analista/RentaEmail';
import { EmptyState } from '../../../components/common/EmptyState';
import { StatusBadge } from '../../../components/common/StatusBadge';
import { Button } from '../../../components/ui/button';
import { TabsContent } from '../../../components/ui/tabs';
import { cn, formatCurrency, formatImporte, getStatusLabel } from '../../../lib/utils';
import { useViewportFillHeight } from '../../../hooks/useViewportFillHeight';
import { AlertCircle, Building2, Check, ChevronDown, ChevronUp, Download, FileText, Home, Pencil, Settings, Upload } from 'lucide-react';

// Input que muestra formato con separadores de miles pero permite editar el valor raw
const FormattedNumberInput = ({ value, onChange, className, ...props }) => {
  const [focused, setFocused] = React.useState(false);
  const displayValue = focused
    ? (value || '')
    : (value ? Number(value).toLocaleString('es-AR', { minimumFractionDigits: 0, maximumFractionDigits: 0 }) : '');
  
  const handleBlurFormatted = () => {
    setTimeout(() => {
      if (!document.hasFocus()) return;
      setFocused(false);
    }, 150);
  };

  return (
    <input
      type="text"
      className={className}
      value={displayValue}
      onChange={(e) => onChange(e.target.value.replace(/[^\d.,-]/g, ''))}
      onFocus={() => setFocused(true)}
      onBlur={handleBlurFormatted}
      {...props}
    />
  );
};

export const TabLiquidacion = ({ edificioId, handleCambiarEstadoGrupo, onAvisoEnviado, calculateGanancia, cn, contratoBadgeByUnidad = {}, cocherasAsociadasMap = {}, cocherasDetalleByMadre = {}, cotizacionPeriodo, edificio, expandedGrupos, formatImporte, getDownloadUrl, getGroupedOrder, handleCambiarEstado, handleFileUpload, handlePublicar, handleTabChange, handleTableChange, selectedPeriodo, sortUnidades, tableData, toggleGrupoExpanded, totales = { ingreso: 0, egreso: 0, fee: 0, ganancia: 0 }, unidades, uploadingFile }) => {
  const esTorreEmilia = edificio?.modelo_administracion === 'gc_factura_y_cobra';
  const PISO1_ID = 'unit_piso1_te';
  const cotizacionFaltante = !(cotizacionPeriodo?.usd_ars > 0);
  // Medición dinámica del alto disponible: ocupar desde el wrapper hasta el fondo del viewport
  const [wrapperRef, scrollHeight] = useViewportFillHeight();
  // Expansión por unidad madre (para mostrar detalle de cocheras asociadas)
  const [expandedMadres, setExpandedMadres] = React.useState({});
  const toggleMadreExpanded = (uid) => setExpandedMadres(prev => ({ ...prev, [uid]: !prev[uid] }));

  // Rent% sobre ganancia: ((ganancia ARS / usd_ars) * 12) / valor_inversion_USD * 100
  const computeRentPct = (unidad, data, tipoAdmin) => {
    const ganancia = calculateGanancia(data, tipoAdmin);
    if (!ganancia || ganancia <= 0) return null;
    const inv = unidad?.monto_inversion;
    if (!inv || !inv.monto || inv.monto <= 0) return null;
    const usdArs = cotizacionPeriodo?.usd_ars || 0;
    const monedaIng = data.moneda || 'ARS';
    const monedaInv = inv.moneda || 'USD';
    // Convertir ganancia a USD
    let gananciaUsd = ganancia;
    if (monedaIng === 'ARS') {
      if (usdArs <= 0) return null;
      gananciaUsd = ganancia / usdArs;
    }
    // Convertir inversión a USD
    let invUsd = inv.monto;
    if (monedaInv === 'ARS') {
      if (usdArs <= 0) return null;
      invUsd = inv.monto / usdArs;
    }
    if (invUsd <= 0) return null;
    return (gananciaUsd * 12) / invUsd * 100;
  };
  return (
        <TabsContent value="carga" className="mt-0">
          {unidades.length === 0 ? (
            <EmptyState
              icon={Home}
              title="Sin unidades"
              description="Primero configura las unidades de este edificio."
              action={
                <Button onClick={() => handleTabChange('config')}>
                  <Settings className="w-4 h-4 mr-2" />
                  Ir a Configuración
                </Button>
              }
            />
          ) : (
            <>
              {/* Table */}
              {(() => {
                // Filter only units in administration
                // Torre Emilia: incluye Piso 1 sintético + excluye sus miembros y componentes del listado plano.
                const piso1MiembrosIds = esTorreEmilia
                  ? new Set(unidades.filter(u => u.conjunto_alquilable_id === 'conj_piso1_te' && u.unidad_id !== PISO1_ID).map(u => u.unidad_id))
                  : new Set();
                const unidadesEnAdmin = esTorreEmilia
                  ? unidades.filter(u => {
                      if (u.unidad_id === PISO1_ID) return true;
                      if (u.en_administracion === false) return false;
                      if (u.rol_unidad === 'componente') return false; // ocultar cocheras hijas
                      if (piso1MiembrosIds.has(u.unidad_id)) return false; // ocultar miembros del Piso 1
                      if (cocherasAsociadasMap[u.unidad_id]) return false; // ocultar cocheras asociadas (van dentro de su oficina madre)
                      return true;
                    })
                  : unidades.filter(u => u.en_administracion !== false && !cocherasAsociadasMap[u.unidad_id]);
                // Check if any unidad has full administration
                const hasFullAdmin = unidadesEnAdmin.some(u => (u.tipo_administracion || 'full') === 'full');

                if (unidadesEnAdmin.length === 0) {
                  return (
                    <div className="bg-slate-50 rounded-lg p-8 text-center">
                      <Building2 className="w-12 h-12 text-slate-300 mx-auto mb-3" />
                      <p className="text-slate-500 font-medium">No hay unidades en administración</p>
                      <p className="text-sm text-slate-400 mt-1">
                        Las unidades marcadas como "fuera de administración" no aparecen aquí
                      </p>
                    </div>
                  );
                }
                
                return (
              <div>
              {cotizacionFaltante && (
                <div className="mb-2 flex items-center gap-2 text-xs text-amber-800 bg-amber-50 border border-amber-200 rounded-md px-3 py-1.5" data-testid="aviso-cotizacion-faltante">
                  <AlertCircle className="w-3.5 h-3.5 flex-shrink-0" />
                  <span>Falta cotización USD para calcular rentabilidad del período</span>
                </div>
              )}
              <div className="bg-white border border-slate-200 rounded-lg overflow-hidden">
                <div ref={wrapperRef} className="overflow-auto" style={{ height: scrollHeight }}>
                  <table className={cn("w-full table-fixed min-h-full", hasFullAdmin ? "min-w-[1050px]" : "min-w-[850px]")}>
                    <thead>
                      <tr className="border-b border-slate-200">
                        <th className="text-left text-xs font-semibold uppercase tracking-wider text-slate-600 py-2.5 px-3 sticky top-0 left-0 bg-slate-50 z-30 w-[24%] shadow-[0_1px_0_0_rgb(226,232,240)]">
                          Unidad
                        </th>
                        <th className="text-center text-xs font-semibold uppercase tracking-wider text-slate-600 py-2.5 px-3 sticky top-0 bg-slate-50 z-20 w-[14%] shadow-[0_1px_0_0_rgb(226,232,240)]">
                          Ingreso
                        </th>
                        {hasFullAdmin && (
                        <th className="text-center text-xs font-semibold uppercase tracking-wider text-slate-600 py-2.5 px-3 sticky top-0 bg-slate-50 z-20 w-[14%] shadow-[0_1px_0_0_rgb(226,232,240)]">
                          Egreso
                        </th>
                        )}
                        <th className="text-center text-xs font-semibold uppercase tracking-wider text-slate-600 py-2.5 px-3 sticky top-0 bg-slate-50 z-20 w-[14%] shadow-[0_1px_0_0_rgb(226,232,240)]">
                          Fee (20%)
                        </th>
                        <th className="text-center text-xs font-semibold uppercase tracking-wider text-slate-600 py-2.5 px-3 sticky top-0 bg-slate-50 z-20 w-[14%] shadow-[0_1px_0_0_rgb(226,232,240)]">
                          Ganancia
                        </th>
                        <th className="text-center text-xs font-semibold uppercase tracking-wider text-slate-600 py-2.5 px-3 sticky top-0 bg-slate-50 z-20 w-[10%] shadow-[0_1px_0_0_rgb(226,232,240)]">
                          Rent %
                        </th>
                        <th className="text-center text-xs font-semibold uppercase tracking-wider text-slate-600 py-2.5 px-3 sticky top-0 bg-slate-50 z-20 w-[10%] shadow-[0_1px_0_0_rgb(226,232,240)]">
                          Estado
                        </th>
                        <th className="text-center text-xs font-semibold uppercase tracking-wider text-slate-600 py-2.5 px-3 sticky top-0 bg-slate-50 z-20 w-[10%] shadow-[0_1px_0_0_rgb(226,232,240)]">
                          Aviso email
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {(esTorreEmilia
                        ? sortUnidades(unidadesEnAdmin).map(u => ({ type: 'unit', unidad: u, grupoId: null }))
                        : getGroupedOrder(sortUnidades([...unidades].filter(u => u.en_administracion !== false)))
                      ).map((entry) => {
                        if (entry.type === 'group_summary') {
                          // Fila resumen del grupo con valores sumados
                          const uids = entry.unidades;
                          const sumIngreso = uids.reduce((s, u) => s + (parseFloat(tableData[u.unidad_id]?.ingreso) || 0), 0);
                          const sumEgreso = uids.reduce((s, u) => s + (parseFloat(tableData[u.unidad_id]?.egreso) || 0), 0);
                          const sumFee = uids.reduce((s, u) => {
                            const d = tableData[u.unidad_id] || {};
                            return s + (parseFloat(d.fee) || 0);
                          }, 0);
                          const sumGanancia = uids.reduce((s, u) => {
                            const d = tableData[u.unidad_id] || {};
                            const t = u.tipo_administracion || 'full';
                            return s + calculateGanancia(d, t);
                          }, 0);
                          const isExpanded = expandedGrupos[entry.grupo.grupo_id];
                          
                          return (
                            <tr key={`lgs-${entry.grupo.grupo_id}`} className="bg-indigo-50 hover:bg-indigo-100/60 font-semibold cursor-pointer" onClick={() => toggleGrupoExpanded(entry.grupo.grupo_id)}>
                              <td className="py-2.5 px-3 sticky left-0 bg-indigo-50 z-10">
                                <span className="inline-flex items-center gap-1.5 text-indigo-900">
                                  {isExpanded ? <ChevronUp className="w-4 h-4 text-indigo-400" /> : <ChevronDown className="w-4 h-4 text-indigo-400" />}
                                  {entry.grupo.nombre}
                                </span>
                              </td>
                              <td className="py-2.5 px-3 text-right font-mono text-sm text-indigo-900 whitespace-nowrap">$ {formatImporte(sumIngreso, 0)}</td>
                              {hasFullAdmin && (
                              <td className="py-2.5 px-3 text-right font-mono text-sm text-red-700 whitespace-nowrap">$ {formatImporte(sumEgreso, 0)}</td>
                              )}
                              <td className="py-2.5 px-3 text-right font-mono text-sm text-indigo-900 whitespace-nowrap">$ {formatImporte(sumFee, 0)}</td>
                              <td className="py-2.5 px-3 text-right font-mono text-sm whitespace-nowrap">
                                <span className={cn(sumGanancia >= 0 ? 'text-green-700' : 'text-red-600')}>
                                  $ {formatImporte(sumGanancia, 0)}
                                </span>
                              </td>
                              <td className="py-2.5 px-3 text-center text-sm font-mono">
                                {(() => {
                                  // Sumar monto_inversion de todas las unidades del grupo
                                  const totalInversion = uids.reduce((s, u) => {
                                    const inv = u.monto_inversion;
                                    return s + ((inv && inv.monto) ? inv.monto : 0);
                                  }, 0);
                                  if (totalInversion <= 0 || sumGanancia <= 0) return <span className="text-slate-400">-</span>;
                                  // Convertir ganancia a USD
                                  const usdArs = cotizacionPeriodo?.usd_ars || 0;
                                  if (usdArs <= 0) return <span className="text-slate-400">-</span>;
                                  const gananciaUsd = sumGanancia / usdArs;
                                  // Rentabilidad = (Ganancia USD * 12) / Inversion USD * 100
                                  const rent = (gananciaUsd * 12) / totalInversion * 100;
                                  return <span className="text-blue-700 font-semibold">{rent.toFixed(2)}%</span>;
                                })()}
                              </td>
                              <td className="py-2.5 px-3 text-center" onClick={(e) => e.stopPropagation()}>
                                {(() => {
                                  const withResult = uids.filter(u => tableData[u.unidad_id]?.resultado_id);
                                  if (withResult.length === 0) return <span className="text-xs text-indigo-400">-</span>;
                                  const estados = withResult.map(u => tableData[u.unidad_id]?.estado || 'borrador');
                                  const allPublicado = estados.every(e => e === 'publicado');
                                  const allBorrador = estados.every(e => e === 'borrador');
                                  const mixto = !allPublicado && !allBorrador;
                                  const estado = allPublicado ? 'publicado' : (allBorrador ? 'borrador' : 'mixto');
                                  return (
                                    <div className="relative inline-block">
                                      {mixto && (
                                        <span className="block text-[9px] text-orange-600 font-semibold mb-0.5" data-testid={`estado-mixto-${entry.grupo.grupo_id}`}>⚠ Mixto</span>
                                      )}
                                      <select
                                        value={mixto ? 'mixto' : estado}
                                        onChange={(e) => {
                                          if (e.target.value === 'mixto') return;
                                          if (handleCambiarEstadoGrupo) handleCambiarEstadoGrupo(entry.grupo.grupo_id, e.target.value);
                                          else withResult.forEach(u => handleCambiarEstado(tableData[u.unidad_id].resultado_id, u.unidad_id, e.target.value));
                                        }}
                                        className={cn(
                                          "text-xs font-medium rounded px-2 py-1.5 pr-6 border cursor-pointer appearance-none",
                                          mixto
                                            ? "bg-orange-50 text-orange-700 border-orange-300"
                                            : estado === 'publicado'
                                              ? "bg-green-50 text-green-700 border-green-300"
                                              : "bg-amber-50 text-amber-700 border-amber-300"
                                        )}
                                        data-testid={`estado-select-grp-${entry.grupo.grupo_id}`}
                                      >
                                        {mixto && <option value="mixto" disabled>Mixto</option>}
                                        <option value="borrador">Borrador</option>
                                        <option value="publicado">Publicado</option>
                                      </select>
                                      <ChevronDown className="w-3 h-3 absolute right-1.5 top-1/2 -translate-y-1/2 pointer-events-none text-slate-400" />
                                    </div>
                                  );
                                })()}
                              </td>
                              <td className="py-2.5 px-2 text-center" onClick={(e) => e.stopPropagation()}>
                                {(() => {
                                  const pub = uids.filter(u => tableData[u.unidad_id]?.estado === 'publicado');
                                  const fechas = pub.map(u => tableData[u.unidad_id]?.renta_email_enviada_at).filter(Boolean).sort();
                                  return (
                                    <AvisoRentaCell edificioId={edificioId} periodo={selectedPeriodo} unidadIds={pub.map(u => u.unidad_id)}
                                      publicado={pub.length > 0} enviadaAt={fechas.length === pub.length ? fechas[fechas.length - 1] : null}
                                      error={pub.map(u => tableData[u.unidad_id]?.renta_email_error).find(Boolean)}
                                      onSent={(at) => onAvisoEnviado?.(pub.map(u => u.unidad_id), at)} testId={`aviso-renta-grp-${entry.grupo.grupo_id}`} />
                                  );
                                })()}
                              </td>
                            </tr>
                          );
                        }
                        const unidad = entry.unidad;
                        const perteneceAGrupo = !!entry.grupoId;
                        // Torre Emilia: Piso 1 ya viene con valores consolidados precargados en tableData
                        // desde el padre (buildTableData). Render normal.
                        const data = tableData[unidad.unidad_id] || {};
                        const tipoAdmin = unidad.tipo_administracion || 'full';
                        const ganancia = calculateGanancia(data, tipoAdmin);
                        const archivos = data.archivos || {};
                        const archivosFaltantes = ['detalle_costo_servicio', 'determinacion_ganancia', 'factura_alquiler']
                          .filter(tipo => !archivos[tipo]);
                        const canPublish = data.resultado_id && archivosFaltantes.length === 0 && data.estado === 'borrador';
                        const facturaProp = data.factura_propietario;
                        const cocherasDetalle = cocherasDetalleByMadre[unidad.unidad_id] || [];
                        const tieneCocheras = cocherasDetalle.length > 0;
                        const isExpanded = !!expandedMadres[unidad.unidad_id];
                        const rentPct = computeRentPct(unidad, data, tipoAdmin);

                        return (
                          <React.Fragment key={unidad.unidad_id}>
                          <tr 
                            className={cn(
                              "border-b border-slate-100 hover:bg-slate-100/50",
                              perteneceAGrupo && "bg-indigo-50/40"
                            )}
                            data-testid={`row-${unidad.unidad_id}`}
                          >
                            <td className={cn(
                              "py-2 px-3 sticky left-0 z-10",
                              perteneceAGrupo ? "bg-indigo-50/40 border-l-4 border-l-indigo-400" : "bg-white"
                            )}>
                              <div className={cn("flex items-start gap-1.5", perteneceAGrupo && "pl-3")}>
                                {tieneCocheras ? (
                                  <button
                                    type="button"
                                    onClick={() => toggleMadreExpanded(unidad.unidad_id)}
                                    className="mt-0.5 text-slate-400 hover:text-slate-700"
                                    data-testid={`toggle-cocheras-${unidad.unidad_id}`}
                                    aria-label="Mostrar/ocultar cocheras asociadas"
                                  >
                                    {isExpanded ? <ChevronUp className="w-3.5 h-3.5" /> : <ChevronDown className="w-3.5 h-3.5" />}
                                  </button>
                                ) : (
                                  <span className="w-3.5 inline-block" />
                                )}
                                <div className="flex-1 min-w-0">
                                  <p className={cn("font-medium", perteneceAGrupo ? "text-indigo-800" : "text-slate-900")}>{unidad.nombre_unidad}</p>
                                  <p className="text-xs text-slate-500">
                                    {getStatusLabel(unidad.tipo)}
                                    {tieneCocheras && (
                                      <span className="ml-1 text-violet-600">· +{cocherasDetalle.length} cochera{cocherasDetalle.length > 1 ? 's' : ''}</span>
                                    )}
                                  </p>
                                </div>
                              </div>
                            </td>
                            <td className="py-2 px-2">
                              <FormattedNumberInput
                                value={data.ingreso || ''}
                                onChange={(v) => handleTableChange(unidad.unidad_id, 'ingreso', v)}
                                className="w-full text-right h-9 text-sm px-3 py-2 rounded-md border border-slate-200 bg-white text-slate-800 font-mono focus:outline-none focus:ring-2 focus:ring-[#1B4D3E]/30 focus:border-[#1B4D3E]"
                                data-testid={`ingreso-${unidad.unidad_id}`}
                                placeholder="-"
                                title="Ingreso editable (por defecto viene del contrato vigente)"
                              />
                              {data.usa_manual === true && (
                                <div className="flex justify-end mt-1">
                                  <span
                                    className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-medium bg-amber-50 text-amber-700 ring-1 ring-amber-200"
                                    data-testid={`chip-manual-${unidad.unidad_id}`}
                                    title="Este ingreso fue cargado/editado manualmente y no proviene del contrato"
                                  >
                                    <Pencil className="w-2.5 h-2.5" />
                                    Manual
                                  </span>
                                </div>
                              )}
                            </td>
                            {hasFullAdmin && (
                            <td className="py-2 px-2">
                              {tipoAdmin === 'full' ? (
                              <FormattedNumberInput
                                value={data.egreso || ''}
                                onChange={(v) => handleTableChange(unidad.unidad_id, 'egreso', v)}
                                className="w-full text-right h-9 text-sm px-3 py-2 rounded-md border border-slate-200 bg-white text-red-700 font-mono focus:outline-none focus:ring-2 focus:ring-[#1B4D3E]/30 focus:border-[#1B4D3E]"
                                data-testid={`egreso-${unidad.unidad_id}`}
                                placeholder="-"
                                title="Egreso editable (por defecto viene de 6. Egresos)"
                              />
                              ) : (
                                <span className="text-xs text-slate-400 block text-center">N/A</span>
                              )}
                            </td>
                            )}
                            <td className="py-2 px-2">
                              <FormattedNumberInput
                                value={data.fee === '' || data.fee == null ? '' : data.fee}
                                onChange={(v) => handleTableChange(unidad.unidad_id, 'fee', v)}
                                className="w-full text-right h-9 text-sm px-3 py-2 rounded-md border border-slate-200 bg-white text-slate-800 font-mono focus:outline-none focus:ring-2 focus:ring-[#1B4D3E]/30 focus:border-[#1B4D3E]"
                                data-testid={`fee-${unidad.unidad_id}`}
                                placeholder="-"
                                title="Fee editable (por defecto se calcula automáticamente; al editar ingreso/egreso se recalcula)"
                              />
                            </td>
                            <td className="py-2 px-2 text-center whitespace-nowrap">
                              <span className={cn(
                                "font-mono text-sm font-semibold",
                                ganancia >= 0 ? "text-green-700" : "text-red-700"
                              )}>
                                {parseFloat(data.ingreso) > 0 ? `$ ${formatImporte(ganancia, 0)}` : '-'}
                              </span>
                            </td>
                            <td className="py-2 px-2">
                              <div className="flex items-center justify-end gap-1">
                                {perteneceAGrupo ? (
                                  <span className="text-slate-300">-</span>
                                ) : (
                                  <span className={cn(
                                    "font-mono text-sm",
                                    rentPct != null ? "text-blue-700 font-semibold" : (cotizacionFaltante ? "text-amber-600" : "text-slate-400")
                                  )}
                                  title={rentPct != null ? "Rent% anual = (Ganancia USD × 12) / Inversión USD" : (cotizacionFaltante ? "Falta cotización USD del período" : "Sin ganancia o sin monto de inversión")}
                                  data-testid={`rentpct-${unidad.unidad_id}`}
                                  >
                                    {rentPct != null ? `${rentPct.toFixed(2)}%` : (cotizacionFaltante ? 'Pendiente USD' : '-')}
                                  </span>
                                )}
                              </div>
                            </td>
                            <td className="py-2 px-2 text-center">
                              {perteneceAGrupo ? (
                                <span className="text-xs text-slate-300">-</span>
                              ) : data.resultado_id ? (
                                <div className="relative inline-block">
                                  <select
                                    value={data.estado || 'borrador'}
                                    onChange={(e) => handleCambiarEstado(data.resultado_id, unidad.unidad_id, e.target.value)}
                                    className={cn(
                                      "text-xs font-medium rounded px-2 py-1.5 pr-6 border cursor-pointer appearance-none",
                                      data.estado === 'publicado'
                                        ? "bg-green-50 text-green-700 border-green-300"
                                        : "bg-amber-50 text-amber-700 border-amber-300"
                                    )}
                                    data-testid={`estado-select-${unidad.unidad_id}`}
                                  >
                                    <option value="borrador">Borrador</option>
                                    <option value="publicado">Publicado</option>
                                  </select>
                                  <ChevronDown className="w-3 h-3 absolute right-1.5 top-1/2 -translate-y-1/2 pointer-events-none text-slate-400" />
                                </div>
                              ) : (() => {
                                // Fallback de estado cuando NO hay resultado guardado.
                                // Permitir publicar/aprobar incluso si solo hay egresos.
                                const ing = parseFloat(data.ingreso) || 0;
                                const eg = Math.abs(parseFloat(data.egreso) || 0);
                                const tieneMovimiento = ing > 0 || eg > 0;
                                if (!tieneMovimiento) {
                                  return (
                                    <span
                                      className="inline-block px-1.5 py-0.5 rounded text-[10px] font-medium ring-1 bg-slate-50 text-slate-500 ring-slate-200"
                                      data-testid={`estado-fallback-${unidad.unidad_id}`}
                                    >
                                      Sin movimiento
                                    </span>
                                  );
                                }
                                // Hay movimiento → mostrar dropdown borrador/publicado funcional
                                return (
                                  <div className="flex flex-col items-center gap-0.5">
                                    <div className="relative inline-block">
                                      <select
                                        defaultValue="borrador"
                                        onChange={(e) => {
                                          // Crea/persiste resultado con el estado seleccionado.
                                          // Dispara guardado vía handleTableChange (existente) + publicar inmediato.
                                          handlePublicar(unidad.unidad_id, e.target.value);
                                        }}
                                        className="text-xs font-medium rounded px-2 py-1.5 pr-6 border cursor-pointer appearance-none bg-amber-50 text-amber-700 border-amber-300"
                                        data-testid={`estado-fallback-select-${unidad.unidad_id}`}
                                      >
                                        <option value="borrador">Borrador</option>
                                        <option value="publicado">Publicado</option>
                                      </select>
                                      <ChevronDown className="w-3 h-3 absolute right-1.5 top-1/2 -translate-y-1/2 pointer-events-none text-slate-400" />
                                    </div>
                                    {ing === 0 && eg > 0 && (
                                      <span className="text-[9px] text-rose-600" title="Solo egresos">Solo egresos</span>
                                    )}
                                  </div>
                                );
                              })()}
                            </td>
                            <td className="py-2 px-2 text-center">
                              {perteneceAGrupo ? <span className="text-xs text-slate-300">-</span> : (
                                <AvisoRentaCell edificioId={edificioId} periodo={selectedPeriodo} unidadIds={[unidad.unidad_id]}
                                  publicado={data.estado === 'publicado'} enviadaAt={data.renta_email_enviada_at} error={data.renta_email_error}
                                  onSent={(at) => onAvisoEnviado?.([unidad.unidad_id], at)} testId={`aviso-renta-${unidad.unidad_id}`} />
                              )}
                            </td>
                          </tr>
                          {tieneCocheras && isExpanded && cocherasDetalle.map((cd) => {
                            const cFee = 0; // El fee de cochera asociada va a la oficina madre (ya incluido)
                            const cGanancia = (cd.ingreso || 0) - (cd.egreso || 0);
                            return (
                              <tr key={`coch-${cd.unidad_id}`} className="bg-violet-50/40 border-b border-slate-100" data-testid={`row-cochera-${cd.unidad_id}`}>
                                <td className="py-1.5 px-3 sticky left-0 bg-violet-50/40 z-10">
                                  <div className="pl-8 flex items-center gap-2">
                                    <span className="text-xs text-violet-700 font-medium">↳ {cd.nombre}</span>
                                    <span className="text-[10px] text-violet-500">cochera asociada</span>
                                  </div>
                                </td>
                                <td className="py-1.5 px-2 text-right font-mono text-xs text-slate-700">
                                  {cd.ingreso > 0 ? formatImporte(cd.ingreso, 0) : '-'}
                                </td>
                                {hasFullAdmin && (
                                <td className="py-1.5 px-2 text-right font-mono text-xs text-slate-700">
                                  {cd.egreso > 0 ? formatImporte(cd.egreso, 0) : '-'}
                                </td>
                                )}
                                <td className="py-1.5 px-2 text-right font-mono text-xs text-slate-400">-</td>
                                <td className="py-1.5 px-2 text-right font-mono text-xs">
                                  <span className={cn(cGanancia >= 0 ? "text-green-700" : "text-red-700")}>
                                    {cd.ingreso > 0 || cd.egreso > 0 ? formatImporte(cGanancia, 0) : '-'}
                                  </span>
                                </td>
                                <td className="py-1.5 px-2 text-right text-xs text-slate-400">-</td>
                                <td className="py-1.5 px-2 text-center text-xs text-slate-400">-</td>
                                <td className="py-1.5 px-2 text-center text-xs text-slate-400">-</td>
                              </tr>
                            );
                          })}
                          </React.Fragment>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </div>
              </div>
                );
              })()}

            </>
          )}
        </TabsContent>
  );
};
